import React, { useRef, useState } from 'react';
import { useToast } from '../hooks/useToast';
import { analyzeImageWithGeminiAPI, uploadImageDirectly } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';

const LiveVideoAnalysisPage: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);

  // Request camera access
  const startCamera = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setHasCamera(true);
      }
    } catch (err) {
      setError('Could not access camera.');
    }
  };

  // Capture frame and analyze
  const handleSnapAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setIsAnalyzing(true);
    setResult(null);
    showToast('info', 'Analyzing...', 'Keep steady while we analyze this for you...');

    // Draw current frame to canvas
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setIsAnalyzing(false);
      showToast('error', 'Error', 'Could not capture image.');
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setIsAnalyzing(false);
        showToast('error', 'Error', 'Could not capture image.');
        return;
      }
      const file = new File([blob], 'snapshot.png', { type: 'image/png' });
      try {
        // First upload the image to get a URL
        const imageUrl = await uploadImageDirectly(file);
        // Then analyze the image using the URL
        const analysis = await analyzeImageWithGeminiAPI(imageUrl);
        setResult(analysis);
        showToast('success', 'Analysis Complete!', 'Here are your results.');
      } catch (err) {
        setResult(null);
        showToast('error', 'Analysis Failed', 'Could not analyze the image.');
      } finally {
        setIsAnalyzing(false);
      }
    }, 'image/png');
  };

  // Stop camera on unmount
  React.useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 flex flex-col items-center justify-center py-12">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 w-full max-w-xl flex flex-col items-center">
        <h1 className="text-3xl font-bold mb-4 text-center text-gray-900 dark:text-white">Live Video Analysis</h1>
        {!hasCamera ? (
          <button
            onClick={startCamera}
            className="btn btn-primary px-6 py-3 rounded-xl text-white bg-blue-600 hover:bg-blue-700 mb-4"
          >
            Grant Camera Access
          </button>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="rounded-xl border border-gray-300 dark:border-gray-700 w-full max-w-md mb-4"
              style={{ aspectRatio: '16/9', background: '#222' }}
            />
            <button
              onClick={handleSnapAndAnalyze}
              disabled={isAnalyzing}
              className="btn btn-primary px-6 py-3 rounded-xl text-white bg-purple-600 hover:bg-purple-700 mb-4 disabled:opacity-50"
            >
              {isAnalyzing ? 'Analyzing...' : 'Snap & Analyze'}
            </button>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {error && <div className="text-red-600 mb-2">{error}</div>}
        {isAnalyzing && <LoadingSpinner />}
        {result && (
          <div className="mt-6 w-full">
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Analysis Result</h2>
            <pre className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 text-sm overflow-x-auto text-gray-800 dark:text-gray-200">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveVideoAnalysisPage; 